import { Input } from "@progress/kendo-react-inputs";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStartChatConversationMutation } from "../../../api/chatApi";
import ActionButton from "../../workspace/components/atoms/actionButton";
import { homeSuggestedQuestions } from "../homeShellData";
import { HomeSectionBody, type HomeSectionStatus } from "./homeSection";
import { SovraSignalIcon } from "../../../chat/chatConversation";

const visibleQuestionCount = 2;
const promptPlaceholder = "What should I discuss with Coles at our next meeting?";
const expandedPanelId = "home-ask-sovra-panel";

type AskSovraPromptProps = {
    status?: HomeSectionStatus;
    questions?: string[];
};

const AskSovraPrompt = ({
    status = "ready",
    questions = homeSuggestedQuestions
}: AskSovraPromptProps) => {
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);
    const [prompt, setPrompt] = useState("");
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [startChatConversation, { isLoading: isSubmittingPrompt }] = useStartChatConversationMutation();
    const isSubmittingPromptRef = useRef(false);

    const visibleQuestions = questions.slice(0, visibleQuestionCount);
    const previewText = prompt.trim() || promptPlaceholder;

    const navigateToConversation = (conversationId: number): void => {
        const searchParams = new URLSearchParams({
            id: conversationId.toString()
        });

        void navigate(`/app/workspace/simulator?${searchParams.toString()}`);
    };

    const submitPrompt = async (text: string): Promise<void> => {
        const trimmedPrompt = text.trim();
        if (!trimmedPrompt || isSubmittingPrompt || isSubmittingPromptRef.current) {
            return;
        }

        isSubmittingPromptRef.current = true;
        setSubmitError(null);

        try {
            const response = await startChatConversation({ text: trimmedPrompt }).unwrap();
            setPrompt("");
            navigateToConversation(response.chat_conversation_id);
        } catch (error) {
            console.error("Error starting simulator conversation from home prompt.", error);
            setSubmitError("Unable to send your question. Try again.");
        } finally {
            isSubmittingPromptRef.current = false;
        }
    };

    const handlePromptFormSubmit = (event: React.SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        void submitPrompt(prompt);
    };

    const handleToggleExpanded = (): void => {
        setIsExpanded(current => !current);
    };

    return (
        <section className="home-ask-panel bg-white flex-shrink-0" aria-label="Ask Sovra">
            <div id={expandedPanelId} className="px-3 pt-4 pb-3" hidden={!isExpanded}>
                <div className="mx-auto w-100" style={{ maxWidth: "46rem" }}>
                    <HomeSectionBody
                        status={status}
                        hasContent={visibleQuestions.length > 0}
                        loadingLabel="Loading suggested questions..."
                        emptyLabel="No suggested questions right now."
                        errorLabel="Unable to load suggested questions."
                        skeletonCount={2}
                    >
                        <div className="d-flex flex-column align-items-start gap-2 mb-3">
                            <p className="text-uppercase fw-semibold !k-text-subtle letter-spacing-wide fs-9 mb-1">
                                Suggested questions
                            </p>
                            {visibleQuestions.map(question => (
                                <button
                                    key={question}
                                    type="button"
                                    className="btn btn-link py-2 px-3 fw-medium fs-8 !k-text-tertiary text-decoration-none text-highlight-brand-hover border border-1 rounded-3 border-custom-hover bg-action-secondary-hover"
                                    disabled={isSubmittingPrompt}
                                    onClick={() => void submitPrompt(question)}
                                >
                                    {question}
                                </button>
                            ))}
                        </div>
                    </HomeSectionBody>

                    <form onSubmit={handlePromptFormSubmit}>
                        <div className="home-ask-shell d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 bg-white rounded-4 ps-3 pe-2 py-2 border-2 k-border-brand-primary shadow-primary-focus-within">
                            <div className="flex-grow-1 min-w-0">
                                <Input
                                    value={prompt}
                                    disabled={isSubmittingPrompt}
                                    className="home-ask-input w-100 fs-7"
                                    placeholder={promptPlaceholder}
                                    aria-label="Ask Sovra"
                                    onChange={event => setPrompt(event.value ?? "")}
                                />
                            </div>
                            <ActionButton
                                variant="primary"
                                type="submit"
                                innerClassName="rounded-3 flex-shrink-0 py-2 border-0"
                                endIcon={<ArrowRight size={16} strokeWidth={2} />}
                                disabled={isSubmittingPrompt}
                            >
                                Ask Sovra
                            </ActionButton>
                        </div>
                    </form>

                    {submitError && <p className="small text-danger mb-0 mt-3 text-center">{submitError}</p>}
                </div>
            </div>

            <button
                type="button"
                className="home-ask-toggle w-100 border-0 bg-white d-flex align-items-center gap-2 px-3 py-3 text-start"
                aria-expanded={isExpanded}
                aria-controls={expandedPanelId}
                onClick={handleToggleExpanded}
            >
                <span className="d-flex align-items-center gap-2 mx-auto w-100 min-w-0" style={{ maxWidth: "46rem" }}>
                    <span className="flex-shrink-0 d-inline-flex" aria-hidden="true">
                        <SovraSignalIcon connected showPulse={false} />
                    </span>
                    <span className="fw-semibold flex-shrink-0 fs-7">Ask Sovra</span>
                    <span className="text-secondary text-truncate min-w-0 flex-grow-1 fs-7">{previewText}</span>
                    {isExpanded
                        ? <ChevronDown size={16} strokeWidth={2} className="flex-shrink-0 text-secondary" aria-hidden="true" />
                        : <ChevronUp size={16} strokeWidth={2} className="flex-shrink-0 text-secondary" aria-hidden="true" />}
                </span>
            </button>
        </section>
    );
};

export default AskSovraPrompt;
